import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Phone, Info, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PushNotificationsCard } from "@/components/PushNotificationsCard";
import { AutopilotCard } from "@/components/AutopilotCard";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyProfilePage() {
  const { user, refresh } = useAuth();
  const [consent, setConsent] = useState({ consent: false, phone: "", last_change_at: null });
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/sms-consent");
        setConsent(data);
        setPhone(data.phone || "");
      } catch (err) { if (typeof console !== 'undefined') console.debug('pages/resident/MyProfilePage.jsx: swallowed', err); }
    })();
  }, [user]);

  const save = async (nextConsent, purpose = "consent") => {
    setSaving(true);
    try {
      const { data } = await api.post("/me/sms-consent", { consent: nextConsent, phone });
      setConsent((prev) => ({ ...prev, ...data, last_change_at: new Date().toISOString() }));
      refresh?.();
      if (purpose === "phone") {
        toast.success("Phone updated");
      } else {
        toast.success(nextConsent ? "SMS alerts enabled" : "SMS alerts disabled");
      }
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  const downloadExport = async () => {
    setExporting(true);
    try {
      const resp = await axios.get(`${API}/api/resident/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const disposition = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(disposition);
      const fname = match ? match[1] : `prestige-export-${Date.now()}.zip`;
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data export is downloading.");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Account" title="My Profile" subtitle="Your resident information & preferences." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6 md:col-span-1 bg-white text-center">
          <Avatar className="h-24 w-24 mx-auto">
            <AvatarImage src={user?.avatar || ""} alt={user?.name} />
            <AvatarFallback className="text-2xl">{(user?.name || "R").slice(0,1)}</AvatarFallback>
          </Avatar>
          <div className="mt-4 text-lg font-semibold font-display">{user?.name}</div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">{user?.role}</div>
        </Card>
        <Card className="p-6 md:col-span-2 bg-white">
          <div className="space-y-3">
            <div><Label>Email</Label><Input value={user?.email || ""} readOnly /></div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 (555) 123-4567" />
              <div className="flex items-center justify-between mt-1 gap-2">
                <div className="text-[11px] text-zinc-500 flex-1">Used only for critical safety alerts (fire, water, evacuations).</div>
                <Button
                  size="sm"
                  data-testid="profile-phone-save"
                  onClick={() => save(consent.consent, "phone")}
                  disabled={saving || (phone || "") === (consent.phone || "")}
                  className="h-7 px-3 rounded-full"
                >
                  {saving ? "Saving…" : "Save phone"}
                </Button>
              </div>
            </div>
            <div><Label>Unit</Label><Input value={user?.unit || "—"} readOnly /></div>
          </div>
        </Card>
      </div>

      <Card className="p-6 mt-4 bg-white" data-testid="sms-consent-card">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2">
              <Phone className="h-3.5 w-3.5" /> Critical SMS alerts
            </div>
            <div className="text-lg font-semibold font-display mt-1">
              {consent.consent ? "You're opted in" : "SMS alerts are off"}
            </div>
            <div className="text-sm text-zinc-600 mt-1">
              We&apos;ll only text you for <b>critical</b> events (life safety, evacuations, water shut-offs). Message &amp; data rates may apply. Reply STOP anytime to opt out.
            </div>
            {consent.last_change_at && (
              <div className="text-[11px] text-zinc-500 mt-2">
                Last change {new Date(consent.last_change_at).toLocaleString()}
              </div>
            )}
          </div>
          <div className="shrink-0 flex flex-col items-end gap-2">
            <Switch
              data-testid="consent-toggle"
              checked={consent.consent}
              disabled={saving}
              onCheckedChange={(v) => save(v)}
            />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{consent.consent ? "on" : "off"}</div>
          </div>
        </div>
        {!consent.consent && (
          <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div>With SMS off, you&apos;ll still get in-app + email alerts. Consider turning it on so you don&apos;t miss safety events when you&apos;re offline.</div>
          </div>
        )}
      </Card>

      <PushNotificationsCard />

      <AutopilotCard />

      <Card className="p-6 mt-4 bg-white" data-testid="data-export-card">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Your data
            </div>
            <div className="text-lg font-semibold font-display mt-1">Export everything we store about you</div>
            <div className="text-sm text-zinc-600 mt-1">
              Download a signed ZIP with your profile, ledger, payments, tickets, ARC requests, violations, pets, bookings, and notifications. The bundle ships with an HMAC signature so you can verify it hasn&apos;t been tampered with.
            </div>
          </div>
          <Button
            data-testid="export-my-data-btn"
            onClick={downloadExport}
            disabled={exporting}
            className="shrink-0 bg-black text-white hover:bg-black/80 rounded-full"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? "Preparing…" : "Export my data"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
